import apis from '@api/client';
import { EligibilityFailureReason, MeEmployeeRequiredActionsEnum } from '@api/dto';
import ErrorDialog from '@components/ErrorDialog';
import { EMPLOYEE_EMAIL_EXISTS_ERROR, EMPLOYEE_NOT_ELIGIBLE_ERROR, REGISTRATION_CODE_EMPLOYEE_MISMATCH_ERROR } from '@constants';
import useAuth from '@contexts/AuthProvider';
import { useRegisterContext } from '@contexts/RegisterProvider';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useRequireAuthentication } from '@pages/RegisterPage/hooks/useRequireAuthentication';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router';

export default function RedeemCode() {
  const navigate = useNavigate();
  const { authenticated } = useAuth();
  const { registrationCode, reset } = useRegisterContext();
  const { loginAgain } = useRequireAuthentication();

  const hasRedeemedRef = useRef(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState('');
  const [errorAction, setErrorAction] = useState<'restart' | 'loginAgain'>('restart');
  const [employeeId, setEmployeeId] = useState<string | null>(null);

  const redeemCodeMutation = useMutation({
    mutationFn: async (code: string) => {
      return apis.registration
        .redeemRegistrationCode({ redeemRegistrationCodeRequest: { registrationCode: code.toUpperCase() } })
        .then(res => res.data);
    },
    onSuccess: ({ employeeId }) => {
      setEmployeeId(employeeId);
    },
    onError: err => {
      if (isAxiosError(err) && err.response?.status === 409 && err.response.data?.type === EMPLOYEE_EMAIL_EXISTS_ERROR) {
        setErrorDialogMessage('errorDialog.messages.employeeEmailAlreadyExists');
      } else if (
        isAxiosError(err) &&
        err.response?.status === 409 &&
        err.response.data?.type === REGISTRATION_CODE_EMPLOYEE_MISMATCH_ERROR
      ) {
        setErrorDialogMessage('errorDialog.messages.registrationCodeEmployeeMismatch');
        setErrorAction('loginAgain');
      } else if (isAxiosError(err) && err.response?.status === 400 && err.response.data?.type === EMPLOYEE_NOT_ELIGIBLE_ERROR) {
        const reason = err.response.data.eligibilityFailureReason as EligibilityFailureReason | undefined;
        setErrorDialogMessage(`completeData.errors.notEligible.${reason ?? EligibilityFailureReason.EligibilityCheckFailed}`);
      } else {
        setErrorDialogMessage('errorDialog.messages.redeemError');
      }
    },
  });

  const getEmployeeQuery = useQuery({
    queryKey: [apis.me.getMyEmployee.name, employeeId],
    queryFn: () => apis.me.getMyEmployee({ employeeId: employeeId! }).then(res => res.data),
    enabled: !!employeeId,
  });

  useEffect(() => {
    if (!registrationCode) void navigate('/');
    if (!authenticated) void navigate('/auth');
  }, [authenticated, navigate, registrationCode]);

  useEffect(() => {
    if (authenticated && registrationCode && !hasRedeemedRef.current) {
      hasRedeemedRef.current = true;
      redeemCodeMutation.mutate(registrationCode);
    }
  }, [authenticated, redeemCodeMutation, registrationCode]);

  useEffect(() => {
    if (!getEmployeeQuery.isSuccess) return;

    const employee = getEmployeeQuery.data;
    if (employee.requiredActions.includes(MeEmployeeRequiredActionsEnum.UpdateProfile)) {
      void navigate('/complete', { state: { employeeId: employee.id } });
    } else {
      void navigate('/success');
    }
  }, [getEmployeeQuery.isSuccess, getEmployeeQuery.data, navigate]);

  const employeeError = getEmployeeQuery.isError ? 'errorDialog.messages.employeeError' : '';

  const restartRegistration = () => {
    reset();
    void navigate('/');
  };

  return (
    <>
      <ErrorDialog
        open={!!(errorDialogMessage || employeeError)}
        body={<Trans i18nKey={errorDialogMessage || employeeError} />}
        primaryAction={
          errorDialogMessage && errorAction === 'loginAgain' ? (
            <Button onClick={loginAgain}>
              <Trans i18nKey="errorDialog.actions.loginAgain" />
            </Button>
          ) : (
            <Button onClick={restartRegistration}>
              <Trans i18nKey="errorDialog.actions.restartRegistration" />
            </Button>
          )
        }
      />
      <Stack sx={{ gap: 3 }}>
        <CircularProgress size={32} />
        <Stack sx={{ gap: 2, textAlign: 'center' }}>
          <Typography variant="h5">
            <Trans i18nKey="redeemCode.title" />
          </Typography>
          <Typography>
            <Trans i18nKey="redeemCode.message" />
          </Typography>
        </Stack>
      </Stack>
    </>
  );
}
